import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, ChevronRight, Clock, Languages, Lock, PlayCircle, Send } from 'lucide-react';
import { learnApi } from '@/api/endpoints';
import { mapDifficulty } from '@/api/mappers';
import type { LearnPostResponse } from '@/api/types';
import { ArticleBlocks } from '@/components/learn/ArticleBlocks';
import { ArticleToc } from '@/components/learn/ArticleToc';
import { formatPostDate } from '@/components/learn/learnFormat';
import { localizedPost, pickLang } from '@/components/learn/learnLocale';
import { EditorialContent } from '@/components/problems/EditorialTab';
import { DifficultyBadge } from '@/components/ui/DifficultyBadge';
import { topicBySlug, topicPath } from '@/data/topics';
import { useLocalized } from '@/hooks/useLocalized';
import { useSeo } from '@/hooks/useSeo';
import { rememberLearnSignupSource, trackEvent } from '@/lib/analytics';
import { cn } from '@/lib/cn';
import { learnSeoDescription, learnSeoTitle } from '@/lib/seoText';
import { LEARN_INDEX_PATH, PAYWALL_CLASS, learnPath, learnPostStructuredData, lessonPath } from '@/lib/structuredData';
import { useInitialData } from '@/ssr/initialData';
import { useAuthStore } from '@/store/auth';
import { NotFound } from './NotFound';

/**
 * Darslik sahifasi: /learn/:slug. Prerender anonim ko'rinishni yozadi (pullik postda matn yo'q);
 * kirgan foydalanuvchi uchun hydration'dan keyin qayta so'raladi (Premium bo'lsa matn ochiladi).
 * Matn tanlangan tilda (tarjima bo'lsa), aks holda o'zbekcha + ogohlantirish; SEO va JSON-LD doim uz.
 */
export function LearnPost() {
  const { t } = useTranslation();
  const { l, lang } = useLocalized();
  const { slug = '' } = useParams();
  const initial = useInitialData();
  const user = useAuthStore((s) => s.user);

  const initialPost = initial?.learnPost?.slug === slug ? initial.learnPost : null;
  const [post, setPost] = useState<LearnPostResponse | null>(initialPost);
  const [notFound, setNotFound] = useState(false);

  // Qulf holati foydalanuvchiga bog'liq (Premium) — boshqa foydalanuvchi kirsa qayta so'raladi
  const viewerKey = `${user?.id ?? ''}`;
  const loadedKey = useRef<string | null>(initialPost ? `${slug}|` : null);

  useEffect(() => {
    const key = `${slug}|${viewerKey}`;
    if (loadedKey.current === key) return;
    // Bepul post foydalanuvchiga bog'liq emas — kirish holati o'zgarsa qayta so'ralmaydi
    if (loadedKey.current?.startsWith(`${slug}|`) && post?.slug === slug && !post.locked && post.free) return;
    let cancelled = false;
    setNotFound(false);
    learnApi
      .detail(slug)
      .then((res) => {
        if (cancelled) return;
        loadedKey.current = key;
        setPost(res.data);
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, viewerKey]);

  const current = post && post.slug === slug ? post : null;

  useEffect(() => {
    trackEvent('article_viewed', { slug });
    rememberLearnSignupSource(slug);
  }, [slug]);

  useSeo({
    title: current ? learnSeoTitle(current.title) : 'SQL darslik — SQLMaster.uz',
    description: current ? (current.metaDescription?.trim() || learnSeoDescription(current)) : undefined,
    path: learnPath(slug),
    // Pullik postda asosiy kontent yo'q — indekslanmaydi (sitemap ham faqat bepullarini beradi)
    noindex: current ? !current.free : false,
    jsonLd: current ? learnPostStructuredData(current) : undefined,
  });

  if (notFound) return <NotFound />;
  if (!current) {
    return <div className="py-24 text-center text-content-secondary">{t('common.loading')}</div>;
  }

  const topic = current.topic ? topicBySlug(current.topic) : undefined;
  const published = formatPostDate(current.publishedAt, lang);
  const view = localizedPost(current, lang);
  // Matn tili sahifa tilidan farq qilsa — ekran o'quvchi va tarjimon to'g'ri talaffuz qilsin
  const textLang = view.contentLang === lang ? undefined : view.contentLang;
  const linkTitle = (x: { title: string; titleRu?: string | null; titleEn?: string | null }) =>
    pickLang(lang, x.title, x.titleRu, x.titleEn);
  // Bloklar o'zbekcha; mavjud tarjima bo'lsa u ko'rsatiladi (indekslangan matnlar yo'qolmasin)
  const blocks = view.contentLang === 'uz' ? current.content?.blocks : undefined;
  const hasBody = Boolean(blocks?.length || view.contentMd);
  const headings = (blocks ?? []).flatMap((b) => (b.type === 'heading' ? [{ id: b.id, text: b.text }] : []));
  const topicArticles = current.topicArticles ?? [];

  return (
    <div className="mx-auto max-w-8xl px-4 py-6 sm:px-6 lg:px-8">
      <nav aria-label={t('problem.breadcrumb')} className="mb-4 text-sm">
        <ol className="flex flex-wrap items-center gap-1.5 text-content-secondary">
          <li>
            <Link to="/" className="transition-colors hover:text-content">
              {t('nav.home')}
            </Link>
          </li>
          <li aria-hidden>
            <ChevronRight className="h-3.5 w-3.5" />
          </li>
          <li>
            <Link to={LEARN_INDEX_PATH} className="transition-colors hover:text-content">
              {t('nav.learn')}
            </Link>
          </li>
          <li aria-hidden>
            <ChevronRight className="h-3.5 w-3.5" />
          </li>
          <li aria-current="page" lang={textLang} className="min-w-0 truncate text-content">
            {view.title}
          </li>
        </ol>
      </nav>

      <div className="grid gap-10 lg:grid-cols-[220px_minmax(0,1fr)] xl:grid-cols-[220px_minmax(0,1fr)_200px]">
        <aside className="order-last space-y-3 lg:sticky lg:top-20 lg:order-first lg:self-start">
          {topic && topicArticles.length > 0 && (
            <nav aria-label={l(topic.title)}>
              <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
                <topic.icon className="h-4 w-4 text-brand-600" aria-hidden />
                {l(topic.title)}
              </h2>
              <ul className="space-y-1 text-sm">
                {topicArticles.map((a) => (
                  <li key={a.slug}>
                    <Link
                      to={learnPath(a.slug)}
                      aria-current={a.slug === current.slug ? 'page' : undefined}
                      className={cn(
                        'block rounded-md px-2 py-1.5 transition-colors',
                        a.slug === current.slug
                          ? 'bg-brand-500/10 font-medium text-brand-600'
                          : 'text-content-secondary hover:bg-surface hover:text-content',
                      )}
                    >
                      {linkTitle(a)}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          )}
          <Link to={LEARN_INDEX_PATH} className="block px-2 text-sm font-medium text-brand-600 hover:underline">
            {t('learn.allPosts')} →
          </Link>
        </aside>

        <article className="min-w-0 max-w-3xl">
          <header className="mb-6">
            <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-content-muted">
              {topic && (
                <Link to={topicPath(topic.slug)} className="tag gap-1 transition-colors hover:text-brand-600">
                  <topic.icon className="h-3.5 w-3.5" aria-hidden />
                  {l(topic.title)}
                </Link>
              )}
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                {t('learn.readingTime', { count: current.readingMinutes })}
              </span>
              {published && <time dateTime={current.publishedAt ?? undefined}>{published}</time>}
            </div>
            <h1 lang={textLang} className="text-2xl font-bold leading-tight sm:text-3xl">
              {view.title}
            </h1>
            <p lang={textLang} className="mt-3 text-lg text-content-secondary">
              {view.summary}
            </p>
          </header>

          {!view.translated && (
            <p
              role="note"
              className="mb-6 flex items-start gap-2 rounded-lg border border-line bg-surface p-3 text-sm text-content-secondary"
            >
              <Languages className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" />
              {t('learn.untranslated')}
            </p>
          )}

          {current.locked || !hasBody ? (
            <div
              className={cn(
                PAYWALL_CLASS,
                'flex flex-col items-center rounded-lg border border-[var(--medium)]/30 bg-[var(--medium)]/10 p-8 text-center',
              )}
            >
              <Lock className="mb-3 h-10 w-10 text-[var(--medium)]" />
              <h2 className="mb-2 text-lg font-semibold">{t('learn.lockedTitle')}</h2>
              <p className="mb-4 max-w-md text-sm text-content-secondary">{t('learn.lockedDesc')}</p>
              <Link to="/pricing" className="btn btn-primary px-5 py-2.5 text-sm font-semibold">
                {t('learn.lockedCta')}
              </Link>
            </div>
          ) : (
            <div lang={textLang}>
              {blocks?.length ? (
                <ArticleBlocks blocks={blocks} articleSlug={current.slug} />
              ) : (
                <EditorialContent contentMd={view.contentMd ?? ''} />
              )}
            </div>
          )}

          {current.problems.length > 0 && (
            <section className="mt-10 rounded-lg border border-brand-500/30 bg-brand-500/5 p-5">
              <h2 className="text-lg font-semibold">{t('learn.practiceTitle')}</h2>
              <p className="mb-3 mt-1 text-sm text-content-secondary">{t('learn.practiceHint')}</p>
              <ul className="card divide-y divide-line">
                {current.problems.map((ex) => (
                  <li key={ex.slug}>
                    <Link
                      to={`/problems/${ex.slug}`}
                      onClick={() => trackEvent('article_problem_clicked', { slug: current.slug, problem: ex.slug })}
                      className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-surface"
                    >
                      <span className="min-w-0 flex-1 truncate font-medium">{linkTitle(ex)}</span>
                      {ex.premium && (
                        <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-[var(--medium)]">
                          <Lock className="h-3 w-3" aria-hidden /> Premium
                        </span>
                      )}
                      <DifficultyBadge difficulty={mapDifficulty(ex.difficulty)} />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {current.courseLesson && (
            <Link
              to={lessonPath(current.courseLesson.courseSlug, current.courseLesson.lessonSlug)}
              onClick={() => trackEvent('article_course_link_clicked', { from: 'article', slug: current.slug })}
              className="card card-hover mt-6 flex items-center gap-3 p-4"
            >
              <PlayCircle className="h-6 w-6 shrink-0 text-brand-600" aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="block text-xs text-content-muted">{t('learn.courseLesson')}</span>
                <span className="block truncate text-sm font-medium">{current.courseLesson.lessonTitle}</span>
              </span>
              <ArrowRight className="h-4 w-4 shrink-0 text-content-muted" aria-hidden />
            </Link>
          )}

          {current.telegramUrl && (
            <a
              href={current.telegramUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-brand-600 hover:underline"
            >
              <Send className="h-4 w-4 text-[#229ED9]" />
              {t('learn.telegramDiscuss')}
            </a>
          )}

          {(current.prev || current.next) && (
            <nav className="mt-10 grid gap-3 border-t border-line pt-6 sm:grid-cols-2" aria-label={t('nav.learn')}>
              {current.prev ? (
                <Link to={learnPath(current.prev.slug)} className="card card-hover flex items-center gap-3 p-4 text-sm">
                  <ArrowLeft className="h-4 w-4 shrink-0 text-content-muted" />
                  <span className="min-w-0">
                    <span className="block text-xs text-content-muted">{t('learn.prev')}</span>
                    <span className="block truncate font-medium">{linkTitle(current.prev)}</span>
                  </span>
                </Link>
              ) : (
                <span />
              )}
              {current.next && (
                <Link
                  to={learnPath(current.next.slug)}
                  className="card card-hover flex items-center justify-end gap-3 p-4 text-right text-sm"
                >
                  <span className="min-w-0">
                    <span className="block text-xs text-content-muted">{t('learn.next')}</span>
                    <span className="block truncate font-medium">{linkTitle(current.next)}</span>
                  </span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-content-muted" />
                </Link>
              )}
            </nav>
          )}
        </article>

        {headings.length > 1 && (
          <aside className="hidden xl:sticky xl:top-20 xl:block xl:self-start">
            <ArticleToc headings={headings} />
          </aside>
        )}
      </div>
    </div>
  );
}
